import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { api } from '@/api/client'
import { isNativePlatform } from '@/platform'

/**
 * A provider condition that blocks publishing on a token that is otherwise
 * live (#2249) — Facebook error 368, "confirm your identity before you can
 * publish as this Page". `status` stays `connected` because the token IS valid
 * and reconnecting cannot clear it, so this is the only signal that every
 * future post to the account will fail. `message` is a finished sentence
 * including the next action; render it, don't rewrite it.
 */
export interface PublishHealth {
  blocked: boolean
  message: string
  at: string | null
}

export interface ConnectedAccount {
  id: string
  platform: string
  account_name: string | null
  // Platform profile picture captured at OAuth connect (third-party CDN URL,
  // can expire — render with an onError fallback). NULL for platforms whose
  // connect flow fetches no picture (e.g. GMB).
  avatar_url: string | null
  // Mirrors backend `ConnectedAccountStatus` exactly. `needs_reauth` is
  // set by the publish worker when the platform returns 401/403 — the UI
  // surfaces a Reconnect banner whenever this value is present.
  status: 'connected' | 'error' | 'pending' | 'disconnected' | 'needs_reauth' | string
  // Absent/null whenever the account can publish.
  publish_health?: PublishHealth | null
  // Features the platform refused this account while it still posts.
  feature_issues?: FeatureIssue[]
}

/**
 * A feature the platform refused this account while it still posts: YouTube
 * custom thumbnails (an unverified channel) or analytics. `message` names the
 * cause and the fix; render it, don't rewrite it.
 */
export interface FeatureIssue {
  feature: string
  message: string
  at: string | null
}

/** The publish block on an account, or null when it can post. */
export function publishBlock(account: ConnectedAccount): PublishHealth | null {
  const health = account.publish_health
  return health && health.blocked && health.message ? health : null
}

interface ConnectedAccountsState {
  accounts: ConnectedAccount[]
  isLoading: boolean
  // Deployment-level "a connect flow can actually succeed" flags, keyed by
  // platform slug (#2341). `null` until fetched (or when the fetch failed) —
  // callers fail OPEN via `isPlatformAvailable` so a transient error never
  // hides a working integration.
  availability: Record<string, boolean> | null
}

interface GoogleAdsLinkResponse {
  invitation_id: string
  status: string
  customer_id: string
}

/**
 * The link attempt's outcome: exactly one of `data` / `error` is set. `error`
 * is the server's own reason (e.g. "Google Ads MCC linking is not configured")
 * — render it as-is instead of substituting a guess about the owner's input
 * (#2341: a "not configured" failure was shown as "Check the customer ID").
 */
export type GoogleAdsLinkResult =
  | { data: GoogleAdsLinkResponse; error: null }
  | { data: null; error: string }

// A Page in the refreshed listing, flagged with whether it's already
// connected — mirrors the shape MetaPagePickerDialog's MetaPageOption
// consumes so the picker can be reused verbatim for the refresh flow (#2550).
export interface MetaRefreshPage {
  id: string
  name: string
  instagram_id: string | null
  instagram_username: string | null
  instagram_profile_picture: string | null
  connected: boolean
}

export type MetaPagesRefreshResult =
  | {
      success: true
      pageCount: number
      // Non-null only when the listing contains at least one Page that isn't
      // connected yet — the caller opens MetaPagePickerDialog with this
      // session id to let the user choose which of them to connect. Refresh
      // alone never creates a ConnectedAccount row (#2550).
      session: string | null
      pages: MetaRefreshPage[]
      error: null
    }
  | { success: false; pageCount: null; session: null; pages: null; error: string }

interface ConnectedAccountsActions {
  fetch: () => Promise<void>
  fetchAvailability: () => Promise<void>
  startOAuth: (platform: string) => Promise<string | null>
  // Accepts either a connected-account id (UUID) or a platform name. Passing
  // an id disconnects exactly that account — required when a business has
  // several accounts of one platform (multi-profile, issue #1225). The
  // platform form returns 409 when it is ambiguous (2+ accounts); we treat
  // that as a failure so nothing is disconnected silently.
  disconnect: (idOrPlatform: string) => Promise<boolean>
  linkGoogleAds: (customerId: string) => Promise<GoogleAdsLinkResult>
  // Re-lists Facebook Pages using the cached long-lived token from the last
  // connect — no OAuth round-trip (#2532). Closes the gap where a Page
  // created or granted after the last connect never shows up until a full
  // reconnect: the merge that recovers those Pages only used to run at
  // connect time. Never silently connects a new Page itself (#2550) — a
  // non-null `session` in the result means the caller must route the user
  // through MetaPagePickerDialog to confirm which of the newly-listed Pages
  // to connect; stale/revoked Pages are pruned unconditionally either way.
  refreshMetaPages: () => Promise<MetaPagesRefreshResult>
}

type ConnectedAccountsStore = ConnectedAccountsState & ConnectedAccountsActions

/**
 * Group connected accounts by platform key. Every surface that needs the full
 * per-platform account list — the Connections sub-rows and the composer /
 * scheduler account picker — reads through this so a business with 2+ accounts
 * of one platform sees them all, instead of only the first (the old
 * `accounts.find(a => a.platform === key)` collapsed multi-profile to one).
 */
export function groupAccountsByPlatform(
  accounts: ConnectedAccount[]
): Record<string, ConnectedAccount[]> {
  const grouped: Record<string, ConnectedAccount[]> = {}
  for (const account of accounts) {
    ;(grouped[account.platform] ??= []).push(account)
  }
  return grouped
}

/** True only for a token that can actually publish — the single source of
 * truth for "postable" across the composer picker and draft editor. */
export function isPostable(status: string): boolean {
  return status === 'connected'
}

/**
 * The connections card's primary action for a given status: `disconnect` when
 * the token is live, `reconnect` for every other state.
 *
 * `pending` deliberately maps to `reconnect` (issue #1559). It used to return
 * its own action, rendered as a permanently DISABLED "Connecting" spinner —
 * an abandoned or failed OAuth left a row nothing in the UI could clear, so
 * the card was bricked until someone edited the database. Anything that isn't
 * a live token is retriable, and every non-connected row is removable.
 */
export function getPrimaryAction(status: string): 'disconnect' | 'reconnect' {
  return status === 'connected' ? 'disconnect' : 'reconnect'
}

/**
 * Whether this deployment can actually connect `platform` (#2341). Fails OPEN:
 * an unfetched/failed availability map (`null`) or an unknown platform key
 * reads as available, so a transient error degrades to today's behavior
 * instead of hiding every card.
 */
export function isPlatformAvailable(
  availability: Record<string, boolean> | null,
  platform: string
): boolean {
  return availability?.[platform] ?? true
}

const initialState: ConnectedAccountsState = {
  accounts: [],
  isLoading: false,
  availability: null,
}

export const useConnectedAccountsStore = create<ConnectedAccountsStore>()(
  devtools(
    (set) => ({
      ...initialState,

      fetch: async () => {
        set({ isLoading: true })
        try {
          // Handle both new envelope {data: [...], meta: {...}} and legacy array
          const response = await api.get<ConnectedAccount[] | { data: ConnectedAccount[] }>(
            '/connected-accounts'
          )
          if (response) {
            const accounts = Array.isArray(response) ? response : response.data
            set({ accounts, isLoading: false })
          } else {
            set({ isLoading: false })
          }
        } catch {
          set({ isLoading: false })
        }
      },

      fetchAvailability: async () => {
        try {
          const response = await api.get<{ data: { platforms: Record<string, boolean> } }>(
            '/connected-accounts/availability'
          )
          set({ availability: response?.data?.platforms ?? null })
        } catch {
          // Fail open — `isPlatformAvailable` treats null as "everything
          // available", which is the pre-#2341 behavior.
          set({ availability: null })
        }
      },

      startOAuth: async (platform: string) => {
        try {
          // `client` decides where the provider callback sends the user back to.
          // Native flows open the dialog in the system browser (no Maxflow
          // session), so the backend must deep-link into the app instead of
          // redirecting to a web route that would demand a fresh login.
          const client = isNativePlatform() ? 'native' : 'web'
          const response = await api.get<{ data: { url: string } }>(
            `/oauth/start?platform=${platform}&client=${client}`
          )
          return response?.data?.url ?? null
        } catch {
          return null
        }
      },

      disconnect: async (idOrPlatform: string) => {
        try {
          await api.delete(`/connected-accounts/${idOrPlatform}`)
          // idOrPlatform is either an account id or a platform name — the two
          // value spaces are disjoint, so filtering on both removes the right
          // row(s) whichever was passed.
          set((state) => ({
            accounts: state.accounts.filter(
              (a) => a.id !== idOrPlatform && a.platform !== idOrPlatform
            ),
          }))
          return true
        } catch {
          // Includes 409 when a platform has multiple connected accounts —
          // the caller must retry with a specific account id.
          return false
        }
      },

      linkGoogleAds: async (customerId: string): Promise<GoogleAdsLinkResult> => {
        try {
          const response = await api.post<{ data: GoogleAdsLinkResponse }>(
            '/oauth/google-ads/link',
            { customer_id: customerId }
          )
          const data = response?.data ?? null
          if (!data) {
            return { data: null, error: 'Failed to send invitation' }
          }
          // Refresh accounts list to pick up the new pending row
          await useConnectedAccountsStore.getState().fetch()
          return { data, error: null }
        } catch (err) {
          // Surface the backend's own reason (ApiError.message carries the
          // server detail, e.g. "Google Ads MCC linking is not configured")
          // instead of collapsing every failure to one generic guess.
          const message =
            err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
              ? err.message
              : null
          return { data: null, error: message || 'Failed to send invitation' }
        }
      },

      refreshMetaPages: async (): Promise<MetaPagesRefreshResult> => {
        try {
          const response = await api.post<{
            data: {
              page_count: number
              new_page_count: number
              session: string | null
              pages: MetaRefreshPage[]
            }
          }>('/oauth/meta/refresh-pages', {})
          const pageCount = response?.data?.page_count ?? 0
          const newPageCount = response?.data?.new_page_count ?? 0
          const session = newPageCount > 0 ? (response?.data?.session ?? null) : null
          // Refresh only prunes/lists on the backend — always re-fetch so any
          // stale/revoked Page pruned by this call disappears from the UI
          // immediately, even when no picker step follows.
          await useConnectedAccountsStore.getState().fetch()
          return {
            success: true,
            pageCount,
            session,
            pages: response?.data?.pages ?? [],
            error: null,
          }
        } catch (err) {
          const message =
            err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
              ? err.message
              : null
          return {
            success: false,
            pageCount: null,
            session: null,
            pages: null,
            error: message || 'Failed to refresh Pages',
          }
        }
      },
    }),
    { name: 'ConnectedAccountsStore', enabled: import.meta.env.DEV }
  )
)
